import React, { useState, useEffect, useRef } from 'react';
import './LazyImage.css';

/**
 * LazyImage
 * Defers image loading until the element is near the viewport.
 * Shows a shimmer skeleton placeholder while loading.
 *
 * Props:
 *  src       – image URL to load lazily
 *  alt       – alt text
 *  fallback  – URL to show if src fails to load
 *  className – class applied to the <img> element
 *  style     – inline style for the wrapper <div>
 *  imgStyle  – inline style for the <img> element
 */
const LazyImage = ({ src, alt, fallback, className, style, imgStyle, ...rest }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasError, setHasError]   = useState(false);
  const [isLoaded, setIsLoaded]   = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    // Use IntersectionObserver for deferred loading.
    // rootMargin: '200px' pre-fetches the image 200px before it enters the viewport.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const resolvedSrc = hasError && fallback ? fallback : src;

  return (
    <div ref={wrapperRef} className="lazy-image-wrapper" style={style}>
      {/* Shimmer skeleton shown until the image finishes loading */}
      {!isLoaded && <div className="lazy-image-skeleton" aria-hidden="true" />}

      {isVisible && (
        <img
          src={resolvedSrc}
          alt={alt}
          loading="lazy"
          className={`lazy-image-img ${isLoaded ? 'lazy-image-loaded' : 'lazy-image-loading'} ${className || ''}`}
          style={imgStyle}
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            setHasError(true);
            setIsLoaded(true);
          }}
          {...rest}
        />
      )}
    </div>
  );
};

export default LazyImage;
